import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';
import { useMemo } from 'react';
import { useGetCarePlansByTreatmentEpisodeIdQuery } from '~/store/services/carePlan';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { CarePlanResponse } from '~/lib/types/carePlan/carePlans';

export const useActivePatientCarePlan = (
  patientId: string,
): {
  isLoading: boolean;
  activeTreatmentEpisode?: TreatmentEpisode;
  activeCarePlan?: CarePlanResponse;
  error?: FetchBaseQueryError | SerializedError;
} => {
  const {
    data: patientTreatmentEpisodes,
    isLoading: isPatientTreatmentEpisodesLoading,
    error: getPatientTreatmentEpisodesError,
  } = useGetPatientTreatmentEpisodesQuery(patientId);
  const activeTreatmentEpisode = useMemo(
    () => patientTreatmentEpisodes?.data.find((episode) => episode.inTreatment),
    [patientTreatmentEpisodes],
  );

  const {
    data: carePlans,
    isLoading: isGetCarePlansLoading,
    error: getCarePlansError,
  } = useGetCarePlansByTreatmentEpisodeIdQuery(
    activeTreatmentEpisode?.treatmentEpisodeId,
    {
      skip: !activeTreatmentEpisode,
    },
  );

  const activeCarePlan = useMemo(
    () =>
      carePlans?.find(
        (pathway) => pathway.status.code === CarePlanStatus.ACTIVE,
      ),
    [carePlans],
  );

  return {
    isLoading: isPatientTreatmentEpisodesLoading || isGetCarePlansLoading,
    activeTreatmentEpisode,
    activeCarePlan,
    error: getPatientTreatmentEpisodesError || getCarePlansError,
  };
};
